import React from "react";
import { CircularProgress, DialogContentText, Typography, DialogTitle, Stack, IconButton } from "@mui/material";
import ModalLayout from "./Layout";
import ModalLayoutHeader from "./Layout/Header";
import ModalLayoutContent from "./Layout/Content";
import ModalLayoutFooter from "./Layout/Footer";
import ModalLayoutBtn from "./Layout/Btn";
import { X, Trash2 } from "react-feather";

/**
 *
 * @param {{
 *  isShown: boolean;
 *  onHide: () => void;
 *  onAction: () => void;
 *  deleting: boolean;
 *  activeBranch: string
 *  filename: string;
 * }} data
 */
const DeleteFileModal = ({
  isShown,
  onHide,
  onAction,
  deleting,
  activeBranch,
  filename,
}) => {
  return (
    <ModalLayout 
      isShown={isShown} 
      onHide={onHide}
      sx={{
        "& .MuiPaper-root": {
          borderRadius: "12px",
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
          overflow: "hidden",
          background: "linear-gradient(to bottom, #ffffff, #f8f9fa)",
          border: "1px solid rgba(0,0,0,0.08)",
          transition: "all 0.3s ease-in-out"
        }
      }}
    >
      <Stack
        direction={"row"}
        justifyContent={"space-between"}
        position={"relative"}
        padding={"16px 24px"}
        borderBottom={"1px solid rgba(0,0,0,0.05)"}
        sx={{ background: "rgba(255,237,237,0.6)" }}
      >
        <DialogTitle
          padding={"0 !important"}
          sx={{
            color: "#e63946",
            fontWeight: 800,
            fontSize: "11px",
            display: "flex",
            alignItems: "center",
            "::before": {
              content: '""',
              display: "inline-block",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              backgroundColor: "#e63946",
              marginRight: "10px",
              animation: "pulse 1.5s infinite",
              "@keyframes pulse": {
                "0%": { boxShadow: "0 0 0 0 rgba(230, 57, 70, 0.7)" },
                "70%": { boxShadow: "0 0 0 10px rgba(230, 57, 70, 0)" },
                "100%": { boxShadow: "0 0 0 0 rgba(230, 57, 70, 0)" }
              }
            }
          }}
        >
          Warning!
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onHide}
          sx={{
            position: "absolute",
            right: 16,
            top: 10,
            color: "#495057",
            "&:hover": {
              background: "rgba(0,0,0,0.05)",
              transform: "rotate(90deg)",
              transition: "transform 0.3s ease"
            }
          }}
        >
          <X size={16} />
        </IconButton>
      </Stack>
      <ModalLayoutContent sx={{ padding: "24px 28px" }}>
        <DialogContentText
          textAlign={"center"}
          color="#4a4a4a"
          sx={{ fontSize: "11px", lineHeight: 1.6 }}
        >
          You are about to delete{" "}
          <Typography
            component={"span"}
            fontWeight={700}
            sx={{ 
              fontSize: "11px",
              color: "#1d3557",
              background: "rgba(241,250,255,0.8)",
              padding: "2px 6px",
              borderRadius: "4px",
              border: "1px solid rgba(0,123,255,0.2)"
            }}
          >
            {filename || ""}
          </Typography>{" "}
          from{" "}
          <Typography
            component={"span"}
            fontWeight={700}
            sx={{ 
              fontSize: "11px",
              color: "#5c6bc0",
              background: "rgba(235,238,252,0.8)",
              padding: "2px 6px",
              borderRadius: "4px",
              border: "1px solid rgba(92,107,192,0.2)"
            }}
          >
            {activeBranch}
          </Typography>
          <br />
          <Typography
            component={"span"}
            fontWeight={700}
            sx={{
              fontSize: "11px",
              color: "#721c24",
              background: "rgba(248,215,218,0.5)",
              display: "block",
              margin: "16px auto 0",
              padding: "10px 12px",
              borderRadius: "6px",
              border: "1px dashed rgba(220,53,69,0.3)",
              maxWidth: "90%",
              animation: "fadeIn 1.1s ease-in",
              "@keyframes fadeIn": {
                "0%": { opacity: 0, transform: "translateY(-5px)" },
                "50%": { opacity: 0.5, transform: "translateY(-5px)" },
                "100%": { opacity: 1, transform: "translateY(0)" }
              }
            }}
          >
            Are you sure?
          </Typography>
        </DialogContentText>
      </ModalLayoutContent>
      <ModalLayoutFooter sx={{ padding: "16px 24px", gap: "12px" }}>
        <ModalLayoutBtn 
          color="default"
          onClick={onHide}
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            boxShadow: "none",
            transition: "all 0.2s",
            padding: "8px 16px",
            color: "#5a6268",
            background: "linear-gradient(45deg, #f5f5f5, #e9ecef)",
            border: "1px solid #dee2e6",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
              background: "linear-gradient(45deg, #e9ecef, #dee2e6)",
            }
          }}
        >
          <X size={16} style={{ marginRight: "6px" }} /> No
        </ModalLayoutBtn>{" "}
        <ModalLayoutBtn 
          color="error" 
          disabled={deleting} 
          onClick={onAction}
          variant="contained"
          sx={{
            fontSize: "11px",
            borderRadius: "8px",
            fontWeight: 600,
            textTransform: "none",
            padding: "8px 20px",
            transition: "all 0.2s",
            background: "linear-gradient(45deg, #d32f2f, #f44336)",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 4px 8px rgba(211,47,47,0.25)"
            },
            "&:disabled": {
              background: "#b0bec5",
              color: "#ffffff"
            }
          }}
        >
          <Trash2 size={16} style={{ marginRight: "6px" }} />{" "}
          {deleting ? (
            <>
              Deleting...&nbsp;&nbsp;
              <CircularProgress size={16} color="inherit" />
            </>
          ) : (
            "Delete"
          )}
        </ModalLayoutBtn>
      </ModalLayoutFooter>
    </ModalLayout>
  );
};

export default DeleteFileModal;
